import axios from 'axios';
import keycloak from '../keycloak';
import {
  Livraison,
  Recuperation,
  Notification,
  StatsChauffeur,
  TrackingGPS,
  Chauffeur,
  ApiResponse,
  FiltresLivraison,
  FiltresRecuperation,
  PhotoLivraison
} from '../types';

// Détection automatique de l'environnement
const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// Configuration de l'URL API
// En local: localhost
// Sur Netlify: utiliser l'URL du backend déployé ou une variable d'env
const API_URL = isLocalDev 
  ? 'http://localhost:8080/api'
  : (process.env.REACT_APP_API_URL || 'https://votre-backend.com/api');

const api = axios.create({
  baseURL: API_URL,
  // Sans délai maximum, une requête émise sous un réseau mobile dégradé reste en attente
  // indéfiniment : le chauffeur voit un bouton figé sur « Envoi en cours » et ne sait pas si
  // son action est passée. 20 s laissent le temps à une 3G lente sans immobiliser l'écran.
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token Keycloak (avec refresh auto)
api.interceptors.request.use(async (config) => {
  if (keycloak.authenticated) {
    try {
      await keycloak.updateToken(30);
      config.headers.Authorization = `Bearer ${keycloak.token}`;
    } catch (err) {
      console.error('Erreur refresh token Keycloak:', err);
      keycloak.logout({ redirectUri: window.location.origin });
    }
  }
  return config;
});

export interface UserProfile {
  subject: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  fullName: string;
  roles: string[];
  authenticated: boolean;
  telephone: string;
}

// ========== AUTH API ==========
export const authAPI = {
  /**
   * DEPRECATED : login custom par téléphone remplacé par Keycloak.
   * Gardé pour compatibilité temporaire uniquement.
   */
  login: async (_telephone: string): Promise<ApiResponse<{ token: string; chauffeur: Chauffeur }>> => {
    return {
      success: false,
      message: 'Connexion directe désactivée. Utilisez Keycloak.',
      data: { token: '', chauffeur: {} as Chauffeur },
    };
  },

  verifyCode: async (_telephone: string, _code: string): Promise<ApiResponse<{ token: string; chauffeur: Chauffeur }>> => {
    return {
      success: false,
      message: 'Connexion directe désactivée. Utilisez Keycloak.',
      data: { token: '', chauffeur: {} as Chauffeur },
    };
  },

  /** Profil utilisateur depuis le JWT Keycloak */
  getMe: async (): Promise<ApiResponse<UserProfile>> => {
    const response = await api.get('/users/me');
    return response.data;
  },

  /** Profil chauffeur métier (lié au compte Keycloak) */
  getProfile: async (): Promise<ApiResponse<Chauffeur>> => {
    const response = await api.get('/chauffeur-auth/profile');
    return response.data;
  },

  updateProfile: async (data: Partial<Chauffeur>): Promise<ApiResponse<Chauffeur>> => {
    const response = await api.put('/chauffeur-auth/profile', data);
    return response.data;
  },
};

// ========== LIVRAISON API ==========
export const livraisonAPI = {
  // Récupérer toutes les livraisons du chauffeur
  getAll: async (filtres?: FiltresLivraison): Promise<ApiResponse<Livraison[]>> => {
    const params = new URLSearchParams();
    if (filtres?.telephone) params.append('telephone', filtres.telephone);
    if (filtres?.statut) params.append('statut', filtres.statut);
    if (filtres?.type) params.append('type', filtres.type);
    if (filtres?.date) params.append('date', filtres.date);
    const response = await api.get(`/chauffeur-workflow/livraisons?${params}`);
    return response.data;
  },

  // Récupérer une livraison par ID
  getById: async (id: number): Promise<ApiResponse<Livraison>> => {
    const response = await api.get(`/chauffeur-workflow/livraisons/${id}`);
    return response.data;
  },

  // Accepter une livraison
  accepter: async (id: number): Promise<ApiResponse<Livraison>> => {
    const response = await api.post(`/chauffeur-workflow/livraisons/${id}/accepter`);
    return response.data;
  },

  // Refuser une livraison
  refuser: async (id: number, motif: string): Promise<ApiResponse<Livraison>> => {
    const response = await api.post(`/chauffeur-workflow/livraisons/${id}/refuser`, { motif });
    return response.data;
  },

  // Marquer comme en route
  enRoute: async (id: number, position?: { latitude: number; longitude: number }): Promise<ApiResponse<Livraison>> => {
    const response = await api.post(`/chauffeur-workflow/livraisons/${id}/en-route`, position || {});
    return response.data;
  },

  // Marquer comme arrivé
  arrive: async (id: number, position?: { latitude: number; longitude: number }): Promise<ApiResponse<Livraison>> => {
    const response = await api.post(`/chauffeur-workflow/livraisons/${id}/arrive`, position || {});
    return response.data;
  },

  // Commencer la livraison
  commencerLivraison: async (id: number): Promise<ApiResponse<Livraison>> => {
    const response = await api.post(`/chauffeur-workflow/livraisons/${id}/commencer-livraison`);
    return response.data;
  },

  // Marquer comme livré
  livrer: async (id: number, data: {
    remarque?: string;
    photos?: PhotoLivraison[];
    signature?: string;
    latitude?: number;
    longitude?: number;
  }): Promise<ApiResponse<Livraison>> => {
    const response = await api.post(`/chauffeur-workflow/livraisons/${id}/livrer`, data);
    return response.data;
  },

  // Déposer au parc (pour livraisons DEPOT_PARC)
  deposerParc: async (id: number, data: {
    remarque?: string;
    position?: { latitude: number; longitude: number };
  }): Promise<ApiResponse<Livraison>> => {
    const response = await api.post(`/chauffeur-workflow/livraisons/${id}/deposer-parc`, data);
    return response.data;
  },

  // Terminer la livraison
  terminer: async (id: number): Promise<ApiResponse<Livraison>> => {
    const response = await api.post(`/chauffeur-workflow/livraisons/${id}/terminer`);
    return response.data;
  },

  // Ajouter une photo
  ajouterPhoto: async (id: number, photo: PhotoLivraison): Promise<ApiResponse<Livraison>> => {
    const response = await api.post(`/chauffeur-workflow/livraisons/${id}/photos`, photo);
    return response.data;
  },

  // Upload de photo (multipart/form-data)
  uploadPhoto: async (id: number, file: File, type: PhotoLivraison['type']): Promise<ApiResponse<string>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    const response = await api.post(`/chauffeur-workflow/livraisons/${id}/photos/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// ========== RÉCUPÉRATION API ==========
export const recuperationAPI = {
  // Récupérer toutes les récupérations du chauffeur
  getAll: async (filtres?: FiltresRecuperation): Promise<ApiResponse<Recuperation[]>> => {
    const params = new URLSearchParams();
    if (filtres?.statut) params.append('statut', filtres.statut);
    if (filtres?.type) params.append('type', filtres.type);
    if (filtres?.date) params.append('date', filtres.date);
    const response = await api.get(`/chauffeur-workflow/recuperations?${params}`);
    return response.data;
  },

  // Récupérer une récupération par ID
  getById: async (id: number): Promise<ApiResponse<Recuperation>> => {
    const response = await api.get(`/chauffeur-workflow/recuperations/${id}`);
    return response.data;
  },

  // Accepter une récupération
  accepter: async (id: number): Promise<ApiResponse<Recuperation>> => {
    const response = await api.post(`/chauffeur-workflow/recuperations/${id}/accepter`);
    return response.data;
  },

  // Refuser une récupération
  refuser: async (id: number, motif: string): Promise<ApiResponse<Recuperation>> => {
    const response = await api.post(`/chauffeur-workflow/recuperations/${id}/refuser`, { motif });
    return response.data;
  },

  // Marquer comme en route
  enRoute: async (id: number, position?: { latitude: number; longitude: number }): Promise<ApiResponse<Recuperation>> => {
    const response = await api.post(`/chauffeur-workflow/recuperations/${id}/en-route`, position);
    return response.data;
  },

  // Marquer comme arrivé
  arrive: async (id: number, position?: { latitude: number; longitude: number }): Promise<ApiResponse<Recuperation>> => {
    const response = await api.post(`/chauffeur-workflow/recuperations/${id}/arrive`, position);
    return response.data;
  },

  // Commencer la récupération
  commencerRecuperation: async (id: number): Promise<ApiResponse<Recuperation>> => {
    const response = await api.post(`/chauffeur-workflow/recuperations/${id}/commencer-recuperation`);
    return response.data;
  },

  // Marquer comme récupéré
  recuperer: async (id: number, data: {
    remarque?: string;
    photos?: PhotoLivraison[];
  }): Promise<ApiResponse<Recuperation>> => {
    const response = await api.post(`/chauffeur-workflow/recuperations/${id}/recuperer`, data);
    return response.data;
  },

  // Terminer la récupération
  terminer: async (id: number): Promise<ApiResponse<Recuperation>> => {
    const response = await api.post(`/chauffeur-workflow/recuperations/${id}/terminer`);
    return response.data;
  },

  // Upload de photo
  uploadPhoto: async (id: number, file: File, type: PhotoLivraison['type']): Promise<ApiResponse<string>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    const response = await api.post(`/chauffeur-workflow/recuperations/${id}/photos/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// ========== SIGNALEMENT API ==========
export interface SignalementRequest {
  missionId: number;
  typeMission: 'LIVRAISON' | 'RECUPERATION';
  message?: string;
  latitude?: number;
  longitude?: number;
}

export interface Signalement {
  id?: number;
  missionId: number;
  typeMission: string;
  chauffeurTelephone?: string;
  numConteneur?: string;
  dateSignalement?: string;
  /** true si un signalement identique venait d'être enregistré : le back-office n'est pas alerté deux fois. */
  doublonIgnore?: boolean;
}

export const signalementAPI = {
  /**
   * Remonte un problème rencontré en mission.
   *
   * Une erreur est propagée telle quelle (réseau, 401, 403, 5xx) : l'appelant ne doit annoncer
   * un succès au chauffeur qu'après confirmation du serveur.
   */
  signaler: async (requete: SignalementRequest): Promise<ApiResponse<Signalement>> => {
    const response = await api.post('/chauffeur-workflow/signalements', requete);
    return response.data;
  },
};

// ========== NOTIFICATION API ==========
export const notificationAPI = {
  // Récupérer toutes les notifications
  getAll: async (): Promise<ApiResponse<Notification[]>> => {
    const response = await api.get('/notifications');
    return response.data;
  },

  // Récupérer les notifications non lues
  getUnread: async (): Promise<ApiResponse<Notification[]>> => {
    const response = await api.get('/notifications/unread');
    return response.data;
  },

  // Marquer comme lue
  markAsRead: async (id: number): Promise<ApiResponse<void>> => {
    const response = await api.post(`/notifications/${id}/read`);
    return response.data;
  },

  // Marquer toutes comme lues
  markAllAsRead: async (): Promise<ApiResponse<void>> => {
    const response = await api.post('/notifications/read-all');
    return response.data;
  },

  // Supprimer une notification
  delete: async (id: number): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/notifications/${id}`);
    return response.data;
  },
};

// ========== STATS API ==========
export const statsAPI = {
  // Récupérer les statistiques du chauffeur
  getStats: async (): Promise<ApiResponse<StatsChauffeur>> => {
    const response = await api.get('/chauffeur-workflow/stats');
    return response.data;
  },

  // Récupérer les statistiques d'une période
  getStatsPeriode: async (debut: string, fin: string): Promise<ApiResponse<StatsChauffeur>> => {
    const response = await api.get('/chauffeur-workflow/stats', { params: { debut, fin } });
    return response.data;
  },
};

// ========== TRACKING GPS API ==========
export const trackingAPI = {
  // Envoyer une position
  sendPosition: async (data: Omit<TrackingGPS, 'id'>): Promise<ApiResponse<void>> => {
    const response = await api.post('/chauffeur-workflow/tracking', data);
    return response.data;
  },

  // Envoyer un batch de positions
  sendPositionsBatch: async (positions: Omit<TrackingGPS, 'id'>[]): Promise<ApiResponse<void>> => {
    const response = await api.post('/chauffeur-workflow/tracking/batch', positions);
    return response.data;
  },

  // Récupérer l'historique d'une livraison
  getHistoriqueLivraison: async (livraisonId: number): Promise<ApiResponse<TrackingGPS[]>> => {
    const response = await api.get(`/chauffeur-workflow/tracking/livraison/${livraisonId}`);
    return response.data;
  },

  // Récupérer l'historique d'une récupération
  getHistoriqueRecuperation: async (recuperationId: number): Promise<ApiResponse<TrackingGPS[]>> => {
    const response = await api.get(`/chauffeur-workflow/tracking/recuperation/${recuperationId}`);
    return response.data;
  },
};

// Export legacy pour compatibilité
export const notificationApi = {
  getNotificationsByChauffeur: async (telephone: string): Promise<Notification[]> => {
    const response = await api.get('/notifications-chauffeur/chauffeur', {
      params: { telephone }
    });
    return response.data;
  },

  updateStatut: async (
    notificationId: number,
    statut: string,
    telephoneChauffeur: string,
    remarque?: string
  ): Promise<Notification> => {
    const response = await api.put(
      `/notifications-chauffeur/${notificationId}/statut`,
      {
        statut,
        telephoneChauffeur,
        remarque: remarque || ''
      }
    );
    return response.data;
  },
};

export default api;
